/**
 * codemirrorDiffExtension.js
 *
 * VS Code-style inline diff for CodeMirror 6:
 *   • Added lines    — green left stripe + green tint + "+" gutter sigil
 *   • Removed lines  — rendered as red strikethrough "ghost" widgets ABOVE the
 *                      line that replaced them (or at end-of-file)
 *   • Per-hunk controls (Phase B) — small Accept/Reject pill rendered as a
 *                      block widget at the top of each pending hunk so the user
 *                      can decide on individual changes inside one edit.
 */

import { StateField, RangeSetBuilder } from "@codemirror/state";
import { EditorView, Decoration, WidgetType, gutter, GutterMarker } from "@codemirror/view";

// ── Line decoration ───────────────────────────────────────────────────────────
const addedLineDeco = Decoration.line({ class: "cm-diff-added" });

// ── Gutter "+" marker ─────────────────────────────────────────────────────────
class PlusMarker extends GutterMarker {
  toDOM() {
    const el = document.createElement("span");
    el.textContent = "+";
    el.className = "cm-diff-gutter-plus";
    return el;
  }
}
const plusMarker = new PlusMarker();

// ── Ghost-line widget for deletions ───────────────────────────────────────────
class GhostLineWidget extends WidgetType {
  constructor(text) {
    super();
    this.text = text;
  }
  eq(other) {
    return other instanceof GhostLineWidget && other.text === this.text;
  }
  toDOM() {
    const wrap = document.createElement("div");
    wrap.className = "cm-diff-ghost-line";
    const sigil = document.createElement("span");
    sigil.className = "cm-diff-ghost-sigil";
    sigil.textContent = "−";
    const txt = document.createElement("span");
    txt.className = "cm-diff-ghost-text";
    txt.textContent = this.text || " ";
    wrap.appendChild(sigil);
    wrap.appendChild(txt);
    return wrap;
  }
  ignoreEvent() { return true; }
}

// ── Per-hunk Accept/Reject controls (Phase B) ─────────────────────────────────
class HunkControlsWidget extends WidgetType {
  constructor(hunkId, label, onAccept, onReject) {
    super();
    this.hunkId = hunkId;
    this.label = label; // e.g. "Change 2 of 4"
    this.onAccept = onAccept;
    this.onReject = onReject;
  }
  eq(other) {
    return other instanceof HunkControlsWidget
      && other.hunkId === this.hunkId
      && other.label === this.label;
  }
  toDOM() {
    const wrap = document.createElement("div");
    wrap.className = "cm-diff-hunk-controls";
    wrap.dataset.hunkId = this.hunkId;

    const label = document.createElement("span");
    label.className = "cm-diff-hunk-label";
    label.textContent = this.label;
    wrap.appendChild(label);

    const accept = document.createElement("button");
    accept.type = "button";
    accept.className = "cm-diff-hunk-btn cm-diff-hunk-accept";
    accept.title = "Accept this change";
    accept.textContent = "✓ Keep";
    accept.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onAccept?.(this.hunkId); });
    wrap.appendChild(accept);

    const reject = document.createElement("button");
    reject.type = "button";
    reject.className = "cm-diff-hunk-btn cm-diff-hunk-reject";
    reject.title = "Reject this change";
    reject.textContent = "✗ Revert";
    reject.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onReject?.(this.hunkId); });
    wrap.appendChild(reject);
    return wrap;
  }
  ignoreEvent() { return false; }
}

// ── Theme ─────────────────────────────────────────────────────────────────────
const diffTheme = EditorView.theme({
  ".cm-diff-added": {
    backgroundColor: "rgba(16, 163, 127, 0.18)",
    borderLeft: "4px solid #10a37f",
  },
  ".cm-diff-gutter": {
    width: "16px",
  },
  ".cm-diff-gutter-plus": {
    color: "#10a37f",
    fontWeight: "700",
    fontSize: "13px",
    lineHeight: "1",
    display: "inline-block",
    width: "100%",
    textAlign: "center",
  },
  ".cm-diff-ghost-line": {
    backgroundColor: "rgba(239, 68, 68, 0.12)",
    borderLeft: "4px solid #ef4444",
    color: "#ef4444",
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "1.4",
    padding: "0 8px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    textDecoration: "line-through",
    textDecorationColor: "rgba(239, 68, 68, 0.6)",
    opacity: "0.9",
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
  },
  ".cm-diff-ghost-sigil": {
    color: "#ef4444",
    fontWeight: "700",
    flexShrink: "0",
    textDecoration: "none",
    width: "12px",
    textAlign: "center",
  },
  ".cm-diff-ghost-text": {
    flex: "1",
    minWidth: "0",
  },
  ".cm-diff-hunk-controls": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 8px",
    margin: "3px 0",
    background: "linear-gradient(to right, rgba(75, 159, 225, 0.18), rgba(75, 159, 225, 0.08))",
    border: "1px solid rgba(75, 159, 225, 0.45)",
    borderRadius: "4px",
    fontSize: "11px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    userSelect: "none",
    minHeight: "22px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  // Always-on scrollbars so the user has a visible affordance to scroll
  // through long diffs (macOS hides them by default which is confusing
  // when reviewing AI changes).
  ".cm-scroller": {
    scrollbarWidth: "thin",
  },
  ".cm-scroller::-webkit-scrollbar": {
    width: "10px",
    height: "10px",
  },
  ".cm-scroller::-webkit-scrollbar-thumb": {
    background: "color-mix(in srgb, var(--photon-chat-muted) 35%, transparent)",
    borderRadius: "5px",
    border: "2px solid transparent",
    backgroundClip: "padding-box",
  },
  ".cm-scroller::-webkit-scrollbar-thumb:hover": {
    background: "color-mix(in srgb, var(--photon-chat-muted) 60%, transparent)",
    backgroundClip: "padding-box",
    border: "2px solid transparent",
  },
  ".cm-scroller::-webkit-scrollbar-track": {
    background: "transparent",
  },
  ".cm-diff-hunk-label": {
    color: "var(--photon-chat-accent)",
    fontWeight: "500",
    flex: "1",
    minWidth: "0",
  },
  ".cm-diff-hunk-btn": {
    cursor: "pointer",
    padding: "2px 8px",
    borderRadius: "3px",
    border: "1px solid transparent",
    fontSize: "11px",
    fontWeight: "500",
    fontFamily: "inherit",
    lineHeight: "1.2",
    background: "transparent",
    transition: "background 0.12s, border-color 0.12s",
  },
  ".cm-diff-hunk-accept": {
    color: "#10a37f",
    borderColor: "rgba(16, 163, 127, 0.35)",
    "&:hover": { background: "rgba(16, 163, 127, 0.15)" },
  },
  ".cm-diff-hunk-reject": {
    color: "#ef4444",
    borderColor: "rgba(239, 68, 68, 0.35)",
    "&:hover": { background: "rgba(239, 68, 68, 0.15)" },
  },
});

// ── Decoration builder ────────────────────────────────────────────────────────
function buildDecoSet(state, addedSet, ghostsByLineNo, hunkAnchors, totalHunks, callbacks) {
  const builder = new RangeSetBuilder();
  const lastLine = state.doc.lines;

  // Build a map of displayLine → hunk anchor for fast lookup.
  const anchorByLine = new Map();
  if (hunkAnchors) {
    for (let i = 0; i < hunkAnchors.length; i++) {
      const a = hunkAnchors[i];
      // Each hunk gets a "Change i of N" label
      anchorByLine.set(a.displayLine, { ...a, index: i });
    }
  }

  for (let i = 1; i <= lastLine; i++) {
    const line = state.doc.line(i);
    // Per-hunk controls — render BEFORE ghosts so the controls sit at the
    // very top of the hunk region.
    const anchor = anchorByLine.get(i);
    if (anchor && callbacks) {
      const label = totalHunks > 1
        ? `Change ${anchor.index + 1} of ${totalHunks}`
        : "AI change";
      builder.add(
        line.from,
        line.from,
        Decoration.widget({
          widget: new HunkControlsWidget(
            anchor.id,
            label,
            callbacks.onAccept,
            callbacks.onReject,
          ),
          side: -2, // before ghost widgets (which use side: -1)
          block: true,
        })
      );
    }
    const ghosts = ghostsByLineNo.get(i);
    if (ghosts && ghosts.length) {
      for (const text of ghosts) {
        builder.add(
          line.from,
          line.from,
          Decoration.widget({ widget: new GhostLineWidget(text), side: -1, block: true })
        );
      }
    }
    if (addedSet.has(i)) {
      builder.add(line.from, line.from, addedLineDeco);
    }
  }
  // End-of-file deletions: keyed under (lastLine + 1)
  const tailGhosts = ghostsByLineNo.get(lastLine + 1);
  if (tailGhosts && tailGhosts.length) {
    const tailLine = state.doc.line(lastLine);
    for (const text of tailGhosts) {
      builder.add(
        tailLine.to,
        tailLine.to,
        Decoration.widget({ widget: new GhostLineWidget(text), side: 1, block: true })
      );
    }
  }
  return builder.finish();
}

/**
 * Returns a CodeMirror extension array that highlights changed lines, shows
 * ghost-line widgets for deleted content, and (optionally) renders per-hunk
 * Accept/Reject controls.
 *
 * @param {number[]} addedLineNos   1-based line numbers (in displayed code) added/changed
 * @param {number[]} _removedLineNos  Deprecated, kept for back-compat
 * @param {Map<number,string[]>=} ghostsByLineNo  displayedLineNo → removed-line texts
 * @param {object=} hunkOptions       Phase B per-hunk controls
 * @param {Array<{id:string, displayLine:number, status:string}>} hunkOptions.anchors
 * @param {number} hunkOptions.totalHunks
 * @param {(id:string)=>void} hunkOptions.onAccept
 * @param {(id:string)=>void} hunkOptions.onReject
 */
export function buildDiffExtension(addedLineNos = [], _removedLineNos = [], ghostsByLineNo = new Map(), hunkOptions = null) {
  if (!addedLineNos.length && !ghostsByLineNo.size) return [];

  const addedSet = new Set(addedLineNos);
  const anchors = hunkOptions?.anchors || null;
  const totalHunks = hunkOptions?.totalHunks ?? (anchors?.length ?? 0);
  const callbacks = hunkOptions
    ? { onAccept: hunkOptions.onAccept, onReject: hunkOptions.onReject }
    : null;

  const decoField = StateField.define({
    create: (state) => buildDecoSet(state, addedSet, ghostsByLineNo, anchors, totalHunks, callbacks),
    update: (decos, tr) => (tr.docChanged ? decos.map(tr.changes) : decos),
    provide: (f) => EditorView.decorations.from(f),
  });

  const plusGutter = gutter({
    class: "cm-diff-gutter",
    lineMarker: (view, line) => {
      const lineNo = view.state.doc.lineAt(line.from).number;
      return addedSet.has(lineNo) ? plusMarker : null;
    },
    initialSpacer: () => plusMarker,
  });

  return [decoField, plusGutter, diffTheme];
}
